/**
 * Shared hero banner for Student Loan Contribution pages.
 *
 * Displays a gradient hero section with greeting, benefit progress tracker,
 * and metric cards. Supports three layout variants based on available data:
 *
 * - Variant 1: Progress tracker + 2 cards (new user, no meaningful lifetime data)
 * - Variant 2: Progress tracker + 3 cards (full data with lifetime totals)
 * - Variant 3: No tracker, 4 equal cards (no benefit year tracking available)
 *
 * Used by both the demo loans index page and the production contribution history.
 */
import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { concat } from '@ember/helper';
import style from 'ember-style-modifier/modifiers/style';
import { eq, or } from 'tio-ui/utilities';
import Heading from 'tio-reimagined/components/heading';
import { formatCurrency } from 'tio-reimagined/format';
import {
  PhGraduationCap,
  PhInfo,
  PhHandshake,
  PhCheckCircle,
  PhPaperPlaneTilt,
  PhClock,
} from 'ember-phosphor-icons';

export interface ContributionHeroSignature {
  Args: {
    /** Time-based greeting, e.g. "Good morning" */
    greeting: string;
    /** User's first name */
    firstName: string;
    /**
     * Layout variant:
     * 1 = tracker + 2 cards (new user)
     * 2 = tracker + 3 cards (full data)
     * 3 = no tracker, 4 equal cards
     */
    variant: 1 | 2 | 3;
    /** Estimated remaining benefit for the year (variants 1 & 2) */
    estimatedRemaining?: number;
    /** Benefit year progress percentage 0–100 (variants 1 & 2) */
    benefitYearProgressPercent?: number;
    /** Benefit year range string, e.g. "10/01/25-10/01/26" */
    benefitYearRange?: string;
    /** Projected annual benefit (variants 1 & 2) */
    projectedAnnual?: number;
    /** Employer contribution year-to-date (all variants) */
    employerContribution: number;
    /** Lifetime employer benefit total (variants 2 & 3) */
    lifetimeBenefit?: number;
    /** Next contribution amount */
    nextContribution: number;
    /** Next contribution date string, e.g. "01/2026" */
    nextContributionDate: string;
    /** Last payment amount (variant 3 only) */
    lastPaymentAmount?: number;
    /** Formatted last payment date (variant 3 only) */
    lastPaymentFormatted?: string;
    /** Callback for "view benefit" link */
    onViewBenefit?: () => void;
  };
}

export default class ContributionHero extends Component<ContributionHeroSignature> {
  handleViewBenefit = () => {
    this.args.onViewBenefit?.();
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="hero-gradient-bg rounded-3xl p-6 relative overflow-hidden">
      {{! Decorative Circles }}
      <div
        class="absolute left-[64%] top-6.5 w-12 h-12 rounded-full pointer-events-none bg-purple-500/10 hero-float"
      ></div>
      <div
        class="absolute left-[66%] -top-14.5 w-30 h-30 rounded-full pointer-events-none bg-purple-500/5 hero-float"
        {{style animation-delay="-18s"}}
      ></div>
      <div
        class="absolute left-[77%] top-1.75 w-16 h-16 rounded-full pointer-events-none bg-purple-500/10 hero-float"
        {{style animation-delay="-35s"}}
      ></div>

      {{! Header with Greeting }}
      <div class="flex items-start justify-between mb-6">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2 text-gray-600 text-base">
            <PhGraduationCap @size={{20}} />
            <span class="font-medium">{{@greeting}}, {{@firstName}}</span>
          </div>
          <Heading @level={{2}} class="text-2xl font-semibold text-gray-900 leading-tight">
            Your Student Loan Contributions
          </Heading>
        </div>
        <button
          type="button"
          class="text-base font-semibold text-gray-900 hover:text-purple-700 underline decoration-solid"
          {{on "click" this.handleViewBenefit}}
        >
          view benefit
        </button>
      </div>

      {{! Metric Grid — layout determined by @variant }}
      {{#if (eq @variant 3)}}
        {{! Variant 3: No tracker, 4 equal cards }}
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 xl:gap-6">
          {{! Employer Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col gap-3">
            <div class="flex flex-col gap-2">
              <div class="text-base font-medium leading-none text-gray-700">
                Employer Contribution
              </div>
              <div class="text-3xl font-semibold leading-none text-gray-900">
                {{formatCurrency @employerContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                this benefit year
              </span>
              <PhHandshake @size={{16}} class="text-gray-600" />
            </div>
          </div>

          {{! Total Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col gap-3">
            <div class="flex flex-col gap-2">
              <div class="text-base font-medium leading-none text-gray-700">
                Total Contribution
              </div>
              <div class="text-3xl font-semibold leading-none text-gray-900">
                {{formatCurrency (or @lifetimeBenefit 0)}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                over lifetime
              </span>
              <PhCheckCircle @size={{16}} class="text-gray-600" />
            </div>
          </div>

          {{! Next Payment Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col gap-3">
            <div class="flex flex-col gap-2">
              <div class="text-base font-medium leading-none text-gray-700">
                Next Payment
              </div>
              <div class="text-3xl font-semibold leading-none text-gray-900">
                {{formatCurrency @nextContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                expected
                {{@nextContributionDate}}
              </span>
              <PhPaperPlaneTilt @size={{16}} class="text-gray-600" />
            </div>
          </div>

          {{! Last Payment Card }}
          {{#if @lastPaymentAmount}}
            <div class="bg-white/50 rounded-2xl p-4 flex flex-col gap-3">
              <div class="flex flex-col gap-2">
                <div class="text-base font-medium leading-none text-gray-700">
                  Last Payment
                </div>
                <div class="text-3xl font-semibold leading-none text-gray-900">
                  {{formatCurrency @lastPaymentAmount}}
                </div>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium leading-none text-gray-600">
                  sent
                  {{@lastPaymentFormatted}}
                </span>
                <PhClock @size={{16}} class="text-gray-600" />
              </div>
            </div>
          {{/if}}
        </div>
      {{else}}
        {{! Variants 1 & 2: Tracker + metric cards }}
        <div
          class="grid grid-cols-1 md:grid-cols-2
            {{if (eq @variant 2) 'xl:grid-cols-4' 'lg:grid-cols-3'}}
            gap-4 lg:gap-6"
        >
          {{! Tracker Card — Remaining Benefit }}
          <div class="md:col-span-2 lg:col-span-1 bg-white/50 rounded-2xl p-4 min-w-0">
            <div class="flex flex-col gap-2.5 pt-2">
              <div class="text-3xl font-semibold leading-none text-gray-900">
                {{formatCurrency (or @estimatedRemaining 0)}}
              </div>
              <div class="flex items-center gap-1 h-4">
                <span class="text-sm font-medium leading-tight text-gray-600">
                  estimated remaining benefit
                </span>
                <PhInfo @size={{16}} class="text-gray-600" aria-hidden="true" />
              </div>
            </div>

            {{! Progress Bar }}
            <div class="flex flex-col gap-2 mt-6">
              <div class="h-3 bg-gray-900/15 rounded-2xl overflow-hidden">
                <div
                  class="h-full bg-gray-900 rounded-2xl transition-all"
                  {{style width=(concat (or @benefitYearProgressPercent 0) "%")}}
                ></div>
              </div>
              <div class="flex flex-col gap-1 text-sm px-0.5 leading-none">
                <span class="text-gray-600">
                  <span class="lowercase font-normal">benefit year</span>
                  <span class="font-semibold"> {{@benefitYearRange}}</span>
                </span>
                <span class="text-gray-700 tracking-wide">
                  <span class="font-medium text-gray-600">eligible up to</span>
                  <span class="font-semibold"> {{formatCurrency (or @projectedAnnual 0)}}</span>
                </span>
              </div>
            </div>
          </div>

          {{! Employer Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col gap-3">
            <div class="flex flex-col gap-2">
              <div class="text-base font-medium leading-none text-gray-700">
                Employer Contribution
              </div>
              <div class="text-3xl font-semibold leading-none text-gray-900">
                {{formatCurrency @employerContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                this benefit year
              </span>
              <PhHandshake @size={{16}} class="text-gray-600" />
            </div>
          </div>

          {{#if (eq @variant 2)}}
            {{! Total Contribution Card (Variant 2 only) }}
            <div class="bg-white/50 rounded-2xl p-4 flex flex-col gap-3">
              <div class="flex flex-col gap-2">
                <div class="text-base font-medium leading-none text-gray-700">
                  Total Contribution
                </div>
                <div class="text-3xl font-semibold leading-none text-gray-900">
                  {{formatCurrency (or @lifetimeBenefit 0)}}
                </div>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium leading-none text-gray-600">
                  lifetime
                </span>
                <PhCheckCircle @size={{16}} class="text-gray-600" />
              </div>
            </div>
          {{/if}}

          {{! Next Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col gap-3">
            <div class="flex flex-col gap-2">
              <div class="text-base font-medium leading-none text-gray-700">
                Next Contribution
              </div>
              <div class="text-3xl font-semibold leading-none text-gray-900">
                {{formatCurrency @nextContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                expected
                {{@nextContributionDate}}
              </span>
              <PhPaperPlaneTilt @size={{16}} class="text-gray-600" />
            </div>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
